/**
 * Plant Loop Layout Generator
 *
 * Generates visual graph layouts from detected plant loops.
 * Positions nodes in a logical arrangement for easy viewing.
 */

import type { Node, Edge, XYPosition } from 'reactflow';
import type { FluidMedium } from '../../hvac/types';
import type {
  PlantNodeData,
  PlantEdgeData,
  PlantLoopSide,
  PlantLoop,
  PlantLoopType,
} from '../types';
import type {
  PlantDetectionResult,
  DetectedPlantLoop,
  DetectedPlantComponent,
  PlantNodeConnection,
} from './types';

/**
 * Layout configuration
 */
const LAYOUT_CONFIG = {
  /** Horizontal spacing between components */
  horizontalSpacing: 200,
  /** Vertical spacing between rows */
  verticalSpacing: 150,
  /** Starting X position */
  startX: 100,
  /** Starting Y position for supply side */
  supplyStartY: 100,
  /** Vertical offset for demand side */
  demandOffset: 350,
  /** Space between loops */
  loopSpacing: 500,
  /** Branch horizontal offset */
  branchOffset: 250,
};

/**
 * Generate unique node ID
 */
function generateNodeId(): string {
  return `node_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Generate unique edge ID
 */
function generateEdgeId(): string {
  return `edge_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get edge color based on loop type
 */
function getLoopEdgeColor(loopType: PlantLoopType): string {
  switch (loopType) {
    case 'chilled-water':
      return '#1E88E5';
    case 'hot-water':
      return '#E53935';
    case 'condenser-water':
      return '#8E24AA';
    case 'ground-loop':
      return '#43A047';
    case 'service-hot-water':
      return '#FB8C00';
    default:
      return '#78909C';
  }
}

/**
 * Create a visual node from a detected plant component
 */
function createPlantNode(
  component: DetectedPlantComponent,
  position: XYPosition,
  loopSide: PlantLoopSide,
  branchIndex: number
): Node<PlantNodeData> {
  return {
    id: generateNodeId(),
    type: 'plantNode',
    position,
    data: {
      componentType: component.componentType,
      name: component.name,
      loopSide,
      branchIndex,
      idfObjectId: component.idfObjectId,
      fields: { ...component.fields },
      hasErrors: false,
      errors: [],
    },
  };
}

/**
 * Create edges from plant node connections
 */
function createPlantEdges(
  connections: PlantNodeConnection[],
  componentToNode: Map<string, Node<PlantNodeData>>,
  loopType: PlantLoopType
): Edge<PlantEdgeData>[] {
  const edges: Edge<PlantEdgeData>[] = [];

  for (const conn of connections) {
    const sourceNode = componentToNode.get(conn.source.idfObjectId);
    const targetNode = componentToNode.get(conn.target.idfObjectId);

    if (!sourceNode || !targetNode) continue;

    const loopSide = conn.source.loopSide ?? conn.target.loopSide ?? 'supply';

    edges.push({
      id: generateEdgeId(),
      source: sourceNode.id,
      target: targetNode.id,
      sourceHandle: conn.source.portId,
      targetHandle: conn.target.portId,
      type: 'smoothstep',
      animated: true,
      style: {
        stroke: getLoopEdgeColor(loopType),
        strokeWidth: 2,
      },
      data: {
        sourcePort: conn.source.portId,
        targetPort: conn.target.portId,
        medium: 'water' as FluidMedium,
        nodeName: conn.nodeName,
        loopSide,
      },
    });
  }

  return edges;
}

/**
 * Layout a single plant loop
 */
function layoutPlantLoop(
  loop: DetectedPlantLoop,
  startY: number,
  componentToNode: Map<string, Node<PlantNodeData>>
): {
  nodes: Node<PlantNodeData>[];
  edges: Edge<PlantEdgeData>[];
  maxY: number;
  loopConfig: PlantLoop;
} {
  const nodes: Node<PlantNodeData>[] = [];
  const allComponents: DetectedPlantComponent[] = [];

  // Layout supply side branches
  const supplyY = startY + LAYOUT_CONFIG.supplyStartY;
  let currentX = LAYOUT_CONFIG.startX;
  let branchIndex = 0;

  for (const branch of loop.supplyBranches) {
    for (const component of branch.components) {
      const node = createPlantNode(
        component,
        { x: currentX, y: supplyY },
        'supply',
        branchIndex
      );
      nodes.push(node);
      componentToNode.set(component.idfObjectId, node);
      allComponents.push(component);
      currentX += LAYOUT_CONFIG.horizontalSpacing;
    }
    branchIndex++;
  }

  // Layout demand side branches
  const demandY = supplyY + LAYOUT_CONFIG.demandOffset;
  currentX = LAYOUT_CONFIG.startX + LAYOUT_CONFIG.branchOffset;
  branchIndex = 0;

  for (const branch of loop.demandBranches) {
    for (const component of branch.components) {
      const node = createPlantNode(
        component,
        { x: currentX, y: demandY },
        'demand',
        branchIndex
      );
      nodes.push(node);
      componentToNode.set(component.idfObjectId, node);
      allComponents.push(component);
      currentX += LAYOUT_CONFIG.horizontalSpacing;
    }
    branchIndex++;
  }

  // Build connections for this loop
  const loopConnections: PlantNodeConnection[] = [];
  const nodeUsage = new Map<string, Array<{
    component: DetectedPlantComponent;
    portId: string;
    direction: 'inlet' | 'outlet';
  }>>();

  for (const comp of allComponents) {
    for (const [nodeName, portId] of comp.nodeConnections) {
      if (!nodeUsage.has(nodeName)) {
        nodeUsage.set(nodeName, []);
      }
      const isInlet = comp.inletNodes.includes(nodeName);
      nodeUsage.get(nodeName)?.push({
        component: comp,
        portId,
        direction: isInlet ? 'inlet' : 'outlet',
      });
    }
  }

  for (const [nodeName, usages] of nodeUsage) {
    const outlets = usages.filter(u => u.direction === 'outlet');
    const inlets = usages.filter(u => u.direction === 'inlet');

    for (const outlet of outlets) {
      for (const inlet of inlets) {
        if (outlet.component.idfObjectId === inlet.component.idfObjectId) continue;

        loopConnections.push({
          nodeName,
          source: {
            componentName: outlet.component.name,
            portId: outlet.portId,
            idfObjectId: outlet.component.idfObjectId,
            loopSide: outlet.component.loopSide,
          },
          target: {
            componentName: inlet.component.name,
            portId: inlet.portId,
            idfObjectId: inlet.component.idfObjectId,
            loopSide: inlet.component.loopSide,
          },
        });
      }
    }
  }

  const edges = createPlantEdges(loopConnections, componentToNode, loop.loopType);

  // Create loop configuration
  const loopConfig: PlantLoop = {
    id: `loop_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`,
    config: {
      name: loop.name,
      type: loop.loopType,
      fluidType: loop.fluidType,
      designExitTemp: loop.setpointTemperature ?? getDefaultTemp(loop.loopType),
      designDeltaT: getDefaultDeltaT(loop.loopType),
      minFlowRate: 'autosize',
      maxFlowRate: 'autosize',
      loadDistribution: 'Optimal',
    },
    supplyBranches: [],
    demandBranches: [],
    supplyInletNode: loop.supplyInletNode,
    supplyOutletNode: loop.supplyOutletNode,
    demandInletNode: loop.demandInletNode,
    demandOutletNode: loop.demandOutletNode,
  };

  const maxY = demandY + LAYOUT_CONFIG.verticalSpacing;

  return { nodes, edges, maxY, loopConfig };
}

/**
 * Get default temperature for loop type
 */
function getDefaultTemp(loopType: PlantLoopType): number {
  switch (loopType) {
    case 'chilled-water':
      return 6.67;
    case 'hot-water':
      return 82.2;
    case 'condenser-water':
      return 29.4;
    case 'ground-loop':
      return 15.6;
    case 'service-hot-water':
      return 60;
    default:
      return 20;
  }
}

/**
 * Get default delta T for loop type
 */
function getDefaultDeltaT(loopType: PlantLoopType): number {
  switch (loopType) {
    case 'chilled-water':
      return 5.56;
    case 'hot-water':
      return 11.1;
    case 'condenser-water':
      return 5.56;
    case 'ground-loop':
      return 5.56;
    case 'service-hot-water':
      return 16.7;
    default:
      return 5.56;
  }
}

/**
 * Layout standalone components
 */
function layoutStandalonePlantComponents(
  components: DetectedPlantComponent[],
  startY: number,
  componentToNode: Map<string, Node<PlantNodeData>>
): { nodes: Node<PlantNodeData>[]; maxY: number } {
  const nodes: Node<PlantNodeData>[] = [];

  if (components.length === 0) {
    return { nodes, maxY: startY };
  }

  let currentX = LAYOUT_CONFIG.startX;
  let currentY = startY;

  for (const component of components) {
    const node = createPlantNode(
      component,
      { x: currentX, y: currentY },
      'supply',
      0
    );
    nodes.push(node);
    componentToNode.set(component.idfObjectId, node);
    currentX += LAYOUT_CONFIG.horizontalSpacing;

    // Wrap to next row
    if (currentX > 1200) {
      currentX = LAYOUT_CONFIG.startX;
      currentY += LAYOUT_CONFIG.verticalSpacing;
    }
  }

  return { nodes, maxY: currentY + LAYOUT_CONFIG.verticalSpacing };
}

/**
 * Result of plant graph generation
 */
export interface GeneratedPlantGraph {
  /** Visual nodes for each loop, keyed by loop ID */
  loopNodes: Map<string, Node<PlantNodeData>[]>;
  /** Visual edges for each loop, keyed by loop ID */
  loopEdges: Map<string, Edge<PlantEdgeData>[]>;
  /** Loop configurations */
  loops: PlantLoop[];
  /** Map from IDF object ID to visual node ID */
  idfToNodeMap: Map<string, string>;
  /** Statistics */
  stats: {
    loopCount: number;
    totalNodeCount: number;
    totalEdgeCount: number;
  };
}

/**
 * Generate complete plant graph from detection result
 */
export function generatePlantGraph(result: PlantDetectionResult): GeneratedPlantGraph {
  const loopNodes = new Map<string, Node<PlantNodeData>[]>();
  const loopEdges = new Map<string, Edge<PlantEdgeData>[]>();
  const loops: PlantLoop[] = [];
  const componentToNode = new Map<string, Node<PlantNodeData>>();

  let currentY = 0;
  let totalNodeCount = 0;
  let totalEdgeCount = 0;

  // Layout each plant loop
  for (const detectedLoop of result.plantLoops) {
    const { nodes, edges, maxY, loopConfig } = layoutPlantLoop(
      detectedLoop,
      currentY,
      componentToNode
    );

    loopNodes.set(loopConfig.id, nodes);
    loopEdges.set(loopConfig.id, edges);
    loops.push(loopConfig);

    totalNodeCount += nodes.length;
    totalEdgeCount += edges.length;

    currentY = maxY + LAYOUT_CONFIG.loopSpacing;
  }

  // Layout standalone components
  if (result.standaloneComponents.length > 0) {
    const { nodes } = layoutStandalonePlantComponents(
      result.standaloneComponents,
      currentY,
      componentToNode
    );

    // Add to first loop or create special standalone "loop"
    if (loops.length > 0 && loops[0]) {
      const firstLoopId = loops[0].id;
      const existingNodes = loopNodes.get(firstLoopId) ?? [];
      loopNodes.set(firstLoopId, [...existingNodes, ...nodes]);
    }

    totalNodeCount += nodes.length;
  }

  // Build IDF to node ID map
  const idfToNodeMap = new Map<string, string>();
  for (const node of componentToNode.values()) {
    if (node.data.idfObjectId) {
      idfToNodeMap.set(node.data.idfObjectId, node.id);
    }
  }

  return {
    loopNodes,
    loopEdges,
    loops,
    idfToNodeMap,
    stats: {
      loopCount: loops.length,
      totalNodeCount,
      totalEdgeCount,
    },
  };
}

/**
 * Generate flat graph (all loops combined) for simple import
 */
export function generateFlatPlantGraph(result: PlantDetectionResult): {
  nodes: Node<PlantNodeData>[];
  edges: Edge<PlantEdgeData>[];
  loops: PlantLoop[];
} {
  const generated = generatePlantGraph(result);

  const allNodes: Node<PlantNodeData>[] = [];
  const allEdges: Edge<PlantEdgeData>[] = [];

  for (const nodes of generated.loopNodes.values()) {
    allNodes.push(...nodes);
  }

  for (const edges of generated.loopEdges.values()) {
    allEdges.push(...edges);
  }

  return {
    nodes: allNodes,
    edges: allEdges,
    loops: generated.loops,
  };
}
